import { useState } from 'react';
import { creditosFinanceiros, CreditoFinanceiro } from '@/mocks/creditosFinanceiros';
import { calcularPontosFinanceiro } from '@/utils/pontosFinanceiro';

interface AdminFinancialCardProps {
  id: number;
  tipo: 'pix' | 'pagamento' | 'cashback';
  imagem: string;
  selectedCampaignId: number | null;
  isSelected: boolean;
  onSelect: () => void;
}

function getDiasParaExpirar(credito: CreditoFinanceiro): number {
  const dataCredito = new Date(credito.dataCredito);
  const dataExpiracao = new Date(dataCredito);
  dataExpiracao.setDate(dataExpiracao.getDate() + Math.min(credito.diasExpiracao, 360));
  const hoje = new Date();
  return Math.ceil((dataExpiracao.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
}

function getDataExpiracao(credito: CreditoFinanceiro): Date {
  const dataCredito = new Date(credito.dataCredito);
  const dataExpiracao = new Date(dataCredito);
  dataExpiracao.setDate(dataExpiracao.getDate() + Math.min(credito.diasExpiracao, 360));
  return dataExpiracao;
}

const CARD_CONFIG = {
  pix: {
    nome: 'PIX',
    descricao: 'Resgate via PIX com valor personalizado',
    tagBase: 'PIX Personalizado',
    acaoLabel: (valor: number) => `Receber R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} via PIX`,
  },
  pagamento: {
    nome: 'Pagamento de Contas',
    descricao: 'Pagamento de contas com valor personalizado',
    tagBase: 'Pagamento Personalizado',
    acaoLabel: (valor: number) => `Pagar conta de R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
  },
  cashback: {
    nome: 'CashBack',
    descricao: 'Receba de volta parte dos seus pontos em compras parceiras',
    tagBase: 'Cashback',
    acaoLabel: () => 'Em breve disponível',
  },
};

export default function AdminFinancialCard({ id, tipo, imagem, selectedCampaignId, isSelected, onSelect }: AdminFinancialCardProps) {
  const config = CARD_CONFIG[tipo];
  const isCashback = tipo === 'cashback';

  const [creditoSelecionadoId, setCreditoSelecionadoId] = useState<string>(
    !isCashback && creditosFinanceiros.length > 0 ? creditosFinanceiros[0].id : ''
  );

  const creditoSelecionado = creditosFinanceiros.find(c => c.id === creditoSelecionadoId) ?? null;
  const diasParaExpirar = creditoSelecionado ? getDiasParaExpirar(creditoSelecionado) : null;
  const dataExpiracao = creditoSelecionado ? getDataExpiracao(creditoSelecionado) : null;
  const dataCredito = creditoSelecionado ? new Date(creditoSelecionado.dataCredito) : null;
  const expirado = diasParaExpirar !== null && diasParaExpirar <= 0;
  const urgente = diasParaExpirar !== null && diasParaExpirar > 0 && diasParaExpirar <= 60;
  const muitoUrgente = diasParaExpirar !== null && diasParaExpirar > 0 && diasParaExpirar <= 30;

  const pontos = creditoSelecionado ? calcularPontosFinanceiro(creditoSelecionado.valor) : 0;

  const semCampanha = selectedCampaignId === null;
  const resgatados = selectedCampaignId ? ((selectedCampaignId * 53 + id * 31) % 180) + 3 : null;

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all h-full flex flex-col relative ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
      {/* Checkbox de seleção */}
      <div className="absolute top-2 left-2 z-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
        />
      </div>

      {/* Imagem */}
      <div className="relative">
        <img
          src={imagem}
          alt={config.nome}
          className="w-full h-48 object-cover object-top"
        />
        {/* Badge de pontos */}
        <div className="absolute top-2 right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg flex items-center gap-1.5">
          <i className="ri-star-fill text-base"></i>
          <span>{pontos.toLocaleString()} XP</span>
        </div>
        {/* Tag de destaque */}
        <div className="absolute bottom-2 left-2 bg-blue-400 text-blue-900 px-2 py-1 rounded-full text-xs font-medium">
          {config.tagBase}
        </div>
        {/* Estoque */}
        <div className="absolute bottom-2 right-4 bg-white bg-opacity-90 px-2 py-1 rounded-full text-xs font-medium">
          {isCashback ? 'Em breve' : `${creditosFinanceiros.length} opções`}
        </div>
      </div>

      {/* Conteúdo */}
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-semibold text-gray-900 mb-2 min-h-[3rem]">
          {isCashback ? config.nome : (creditoSelecionado ? `${config.nome} — ${creditoSelecionado.descricao}` : config.nome)}
        </h3>
        <p className="text-gray-600 text-sm mb-3 line-clamp-2 min-h-[2.5rem]">
          {isCashback
            ? config.descricao
            : creditoSelecionado
            ? `Campanha: ${creditoSelecionado.campanha} • ${config.descricao}`
            : config.descricao}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
            {config.tagBase}
          </span>
          {creditoSelecionado && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
              {creditoSelecionado.campanha}
            </span>
          )}
        </div>

        {/* Seletor de crédito */}
        {!isCashback && creditosFinanceiros.length > 0 && (
          <div className="mb-3">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">
              Selecione o bônus para vincular
            </label>
            <div className="relative">
              <select
                value={creditoSelecionadoId}
                onChange={e => setCreditoSelecionadoId(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white cursor-pointer font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
              >
                {creditosFinanceiros.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.descricao} — R$ {c.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ({c.campanha})
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-500">
                <i className="ri-arrow-down-s-line text-lg"></i>
              </div>
            </div>
          </div>
        )}

        {/* Ação e pontos */}
        {!isCashback && creditoSelecionado && (
          <div className="mb-2">
            <p className="text-sm text-gray-700 font-medium">
              {config.acaoLabel(creditoSelecionado.valor)}
            </p>
            <p className="text-xl font-bold text-orange-600 mt-0.5">
              {pontos.toLocaleString('pt-BR')}
              <span className="text-sm text-gray-500 ml-1 font-normal">pts</span>
            </p>
          </div>
        )}

        {/* Data de crédito e expiração */}
        {!isCashback && creditoSelecionado && dataCredito && dataExpiracao && (
          <div className="pt-2 border-t border-gray-100 space-y-1.5 mb-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-gray-500 text-sm"></i>
              </div>
              <span>
                Creditado em <strong className="text-gray-800">{dataCredito.toLocaleDateString('pt-BR')}</strong>
              </span>
            </div>
            <div className={`flex items-center gap-2 text-sm font-medium ${expirado || muitoUrgente ? 'text-red-600' : urgente ? 'text-amber-600' : 'text-gray-600'}`}>
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${expirado || muitoUrgente ? 'ri-time-line text-red-500' : urgente ? 'ri-alarm-warning-line text-amber-500' : 'ri-timer-line text-gray-500'} text-sm`}></i>
              </div>
              {expirado ? (
                <span className="text-red-600 font-semibold">Crédito expirado</span>
              ) : (
                <span>
                  Expira em <strong>{diasParaExpirar} dia{diasParaExpirar !== 1 ? 's' : ''}</strong> ({dataExpiracao.toLocaleDateString('pt-BR')})
                </span>
              )}
            </div>
            {urgente && !expirado && (
              <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-semibold ${muitoUrgente ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-amber-100 text-amber-700 border border-amber-200'}`}>
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className={`${muitoUrgente ? 'ri-alarm-warning-fill' : 'ri-error-warning-fill'} text-sm`}></i>
                </div>
                <span>
                  {muitoUrgente ? '⚠️ Atenção! Menos de 30 dias para expirar' : '⏰ Expira em menos de 60 dias'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Cashback — em breve */}
        {isCashback && (
          <div className="mb-2">
            <p className="text-sm text-gray-500 italic">Funcionalidade em breve disponível</p>
          </div>
        )}

        {/* Resgatados por campanha */}
        {resgatados !== null && (
          <p className="text-sm text-gray-500 mb-3">
            <i className="ri-user-received-line mr-1"></i>
            {resgatados} resgatados nesta campanha
          </p>
        )}

        {/* Status de campanha */}
        <div className="mt-auto pt-2">
          {semCampanha ? (
            <div className="w-full py-2.5 rounded-lg bg-gray-100 text-gray-400 font-semibold text-sm flex items-center justify-center gap-2 cursor-not-allowed">
              <i className="ri-information-line"></i>
              Selecione uma campanha
            </div>
          ) : (
            <div className="w-full py-2.5 rounded-lg bg-blue-50 text-blue-700 font-semibold text-sm flex items-center justify-center gap-2 border border-blue-200">
              <i className="ri-checkbox-circle-line"></i>
              Pronto para vincular
            </div>
          )}
        </div>
      </div>
    </div>
  );
}